import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { waitingApi } from "../lib/api";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import {
  Clock,
  AlertTriangle,
  DollarSign,
  Users,
  UserCheck,
  Palette,
  Building2,
  Timer,
  RefreshCw,
  ExternalLink,
  CheckCircle2,
  FileText,
  Eye,
  MessageSquare,
  Settings,
  Trash2,
  Filter,
} from "lucide-react";

const PARTY_CONFIG = {
  client: { label: "Client", icon: Users, color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/30" },
  pm: { label: "PM/Team", icon: UserCheck, color: "text-green-400", bg: "bg-green-500/10", border: "border-green-500/30" },
  artist: { label: "Artist", icon: Palette, color: "text-purple-400", bg: "bg-purple-500/10", border: "border-purple-500/30" },
  partner: { label: "Partner", icon: Building2, color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/30" },
};

const WaitingManagementPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [filterParty, setFilterParty] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterClient, setFilterClient] = useState("all");
  const [filterProject, setFilterProject] = useState("all");
  
  // Settings dialog
  const [settingsRequest, setSettingsRequest] = useState(null);
  const [requestSettings, setRequestSettings] = useState({
    response_time_hours: 48,
    penalty_rate_per_day: 0.5,
    grace_period_days: 0,
  });

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const res = await waitingApi.getDashboard({});
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Get unique values for filters
  const filterOptions = useMemo(() => {
    if (!data?.requests) return { clients: [], projects: [], types: [] };
    
    const clients = [...new Set(data.requests.map(r => r.client_name).filter(Boolean))].sort();
    const projects = [...new Set(data.requests.map(r => r.project_name).filter(Boolean))].sort();
    const types = [...new Set(data.requests.map(r => r.request_type).filter(Boolean))];
    
    return { clients, projects, types };
  }, [data?.requests]);

  // Filtered requests
  const filteredRequests = useMemo(() => {
    if (!data?.requests) return [];
    return data.requests.filter(req => {
      if (filterParty !== "all" && req.ball_in_court !== filterParty) return false;
      if (filterStatus === "overdue" && !req.is_overdue) return false;
      if (filterStatus === "pending" && req.is_overdue) return false;
      if (filterType !== "all" && req.request_type !== filterType) return false;
      if (filterClient !== "all" && req.client_name !== filterClient) return false;
      if (filterProject !== "all" && req.project_name !== filterProject) return false;
      return true;
    });
  }, [data?.requests, filterParty, filterStatus, filterType, filterClient, filterProject]);

  // Stats
  const stats = useMemo(() => {
    if (!data?.summary) return { pending: 0, overdue: 0, penalty: 0 };
    return {
      pending: data.summary.total_pending || 0,
      overdue: data.summary.overdue_count || 0,
      penalty: data.summary.total_potential_penalty || 0,
    };
  }, [data?.summary]);

  const formatTime = (hours) => {
    if (!hours) return "—";
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    if (hours < 24) return `${Math.round(hours)}h`;
    const days = Math.floor(hours / 24);
    const remainingHours = Math.round(hours % 24);
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  };

  const formatDeadline = (deadline) => {
    const d = new Date(deadline);
    return d.toLocaleDateString("en-US", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  const getRequestTypeLabel = (type) => {
    switch (type) {
      case "info_request": return "Info";
      case "work_review": return "Review";
      case "approval_request": return "Approval";
      case "payment_request": return "Payment";
      case "scope_approval": return "Scope";
      default: return type || "Request";
    }
  };

  const getRequestTypeIcon = (type) => {
    switch (type) {
      case "info_request": return <FileText className="h-3.5 w-3.5 text-sky-400" />;
      case "work_review": return <Eye className="h-3.5 w-3.5 text-orange-400" />;
      case "approval_request": return <CheckCircle2 className="h-3.5 w-3.5 text-green-400" />;
      case "payment_request": return <DollarSign className="h-3.5 w-3.5 text-amber-400" />;
      case "scope_approval": return <AlertTriangle className="h-3.5 w-3.5 text-purple-400" />;
      default: return <MessageSquare className="h-3.5 w-3.5 text-zinc-400" />;
    }
  };

  const openSettings = (req) => {
    setSettingsRequest(req);
    setRequestSettings({
      response_time_hours: req.response_time_hours || 48,
      penalty_rate_per_day: req.penalty_rate_per_day || 0.5,
      grace_period_days: req.grace_period_days || 0,
    });
  };

  const saveSettings = async () => {
    try {
      await api.put(`/client-requests/${settingsRequest.id}/settings`, requestSettings);
      toast.success("Settings saved");
      setSettingsRequest(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to save settings");
    }
  };

  const deleteRequest = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    try {
      await api.delete(`/client-requests/${id}`);
      toast.success("Request deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete request");
    }
  };

  const clearFilters = () => {
    setFilterParty("all");
    setFilterStatus("all");
    setFilterType("all");
    setFilterClient("all");
    setFilterProject("all");
  };

  const hasActiveFilters = filterParty !== "all" || filterStatus !== "all" || filterType !== "all" || filterClient !== "all" || filterProject !== "all";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50">Waiting Management</h1>
          <p className="text-sm text-zinc-500">Track response times and delays across all parties</p>
        </div>
        <Button variant="outline" onClick={fetchData} className="border-zinc-700">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards - Compact */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-yellow-500/10 border-yellow-500/20">
          <CardContent className="p-4 flex items-center gap-3">
            <Timer className="h-8 w-8 text-yellow-400" />
            <div>
              <p className="text-xs text-zinc-500 uppercase">Pending</p>
              <p className="text-2xl font-bold text-yellow-400">{stats.pending}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-red-500/10 border-red-500/20">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="h-8 w-8 text-red-400" />
            <div>
              <p className="text-xs text-zinc-500 uppercase">Overdue</p>
              <p className="text-2xl font-bold text-red-400">{stats.overdue}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-amber-500/10 border-amber-500/20">
          <CardContent className="p-4 flex items-center gap-3">
            <DollarSign className="h-8 w-8 text-amber-400" />
            <div>
              <p className="text-xs text-zinc-500 uppercase">Penalty</p>
              <p className="text-2xl font-bold text-amber-400">${stats.penalty.toFixed(0)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-800/50 border-zinc-700">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500 uppercase mb-2">By Party</p>
            <div className="flex gap-2 flex-wrap">
              {Object.entries(data?.summary?.by_party || {}).map(([party, count]) => {
                const config = PARTY_CONFIG[party];
                if (!config || count === 0) return null;
                return (
                  <Badge key={party} className={cn("text-xs", config.bg, config.color, config.border)}>
                    {config.label}: {count}
                  </Badge>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-zinc-500">
          <Filter className="h-4 w-4" />
          <span className="text-sm">Filters:</span>
        </div>
        
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[130px] bg-zinc-900 border-zinc-700 h-9">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all" className="text-zinc-50">All Status</SelectItem>
            <SelectItem value="pending" className="text-yellow-400">On Time</SelectItem>
            <SelectItem value="overdue" className="text-red-400">Overdue</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[130px] bg-zinc-900 border-zinc-700 h-9">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all" className="text-zinc-50">All Types</SelectItem>
            <SelectItem value="info_request" className="text-sky-400">Info Request</SelectItem>
            <SelectItem value="work_review" className="text-orange-400">Work Review</SelectItem>
            <SelectItem value="approval_request" className="text-green-400">Approval</SelectItem>
            <SelectItem value="payment_request" className="text-amber-400">Payment</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterParty} onValueChange={setFilterParty}>
          <SelectTrigger className="w-[140px] bg-zinc-900 border-zinc-700 h-9">
            <SelectValue placeholder="Ball In Court" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all" className="text-zinc-50">All Parties</SelectItem>
            <SelectItem value="client" className="text-blue-400">Client</SelectItem>
            <SelectItem value="pm" className="text-green-400">PM/Team</SelectItem>
            <SelectItem value="artist" className="text-purple-400">Artist</SelectItem>
            <SelectItem value="partner" className="text-orange-400">Partner</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterClient} onValueChange={setFilterClient}>
          <SelectTrigger className="w-[160px] bg-zinc-900 border-zinc-700 h-9">
            <SelectValue placeholder="Client" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all" className="text-zinc-50">All Clients</SelectItem>
            {filterOptions.clients.map(c => (
              <SelectItem key={c} value={c} className="text-zinc-50">{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterProject} onValueChange={setFilterProject}>
          <SelectTrigger className="w-[160px] bg-zinc-900 border-zinc-700 h-9">
            <SelectValue placeholder="Project" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all" className="text-zinc-50">All Projects</SelectItem>
            {filterOptions.projects.map(p => (
              <SelectItem key={p} value={p} className="text-zinc-50">{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-zinc-400 hover:text-zinc-50 h-9">
            Clear all
          </Button>
        )}
        
        <div className="ml-auto text-sm text-zinc-500">
          Showing {filteredRequests.length} of {data?.requests?.length || 0}
        </div>
      </div>

      {/* Requests Table */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800">
              <TableHead className="text-zinc-400">Request</TableHead>
              <TableHead className="text-zinc-400">Client</TableHead>
              <TableHead className="text-zinc-400">Project</TableHead>
              <TableHead className="text-zinc-400">Type</TableHead>
              <TableHead className="text-zinc-400">Ball In Court</TableHead>
              <TableHead className="text-zinc-400">Status</TableHead>
              <TableHead className="text-zinc-400">Timer</TableHead>
              <TableHead className="text-zinc-400">Deadline</TableHead>
              <TableHead className="text-zinc-400">Penalty</TableHead>
              <TableHead className="text-zinc-400 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8 text-zinc-500">
                  No pending requests found
                </TableCell>
              </TableRow>
            ) : (
              filteredRequests.map((req) => {
                const partyConfig = PARTY_CONFIG[req.ball_in_court] || PARTY_CONFIG.client;
                const PartyIcon = partyConfig.icon;
                const waitingHours = req.waiting_days * 24;
                const overdueHours = req.overdue_days * 24;
                
                return (
                  <TableRow 
                    key={req.id} 
                    className="border-zinc-800 cursor-pointer hover:bg-zinc-800/50"
                    onClick={() => navigate(`/client-requests/${req.id}`)}
                  >
                    <TableCell>
                      <div>
                        <p className="text-zinc-50 font-medium hover:text-orange-400 transition-colors">{req.title}</p>
                        {req.action_label && (
                          <p className="text-xs text-zinc-500">{req.action_label}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-zinc-300">{req.client_name || "—"}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-zinc-400 text-sm">{req.project_name || "—"}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        {getRequestTypeIcon(req.request_type)}
                        <span className="text-sm text-zinc-400">
                          {getRequestTypeLabel(req.request_type)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={cn("text-xs gap-1", partyConfig.bg, partyConfig.color, partyConfig.border)}>
                        <PartyIcon className="h-3 w-3" />
                        {partyConfig.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {req.is_overdue ? (
                        <Badge variant="outline" className="bg-red-500/10 text-red-400 border-red-500/30 text-xs">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Overdue
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-yellow-500/10 text-yellow-400 border-yellow-500/30 text-xs">
                          <Clock className="h-3 w-3 mr-1" />
                          Pending
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className={cn(
                        "flex items-center gap-1.5 px-2 py-1 rounded text-xs font-mono w-fit",
                        req.is_overdue 
                          ? "bg-red-500/20 text-red-400" 
                          : "bg-zinc-800 text-zinc-400"
                      )}>
                        <Clock className="h-3 w-3" />
                        {req.is_overdue ? (
                          <span>+{formatTime(overdueHours)}</span>
                        ) : (
                          formatTime(waitingHours)
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={cn(
                        "text-sm",
                        req.is_overdue ? "text-red-400" : "text-zinc-400"
                      )}>
                        {formatDeadline(req.deadline)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {req.penalty_amount > 0 ? (
                        <span className="text-amber-400 font-mono text-sm">${req.penalty_amount.toFixed(2)}</span>
                      ) : (
                        <span className="text-zinc-600">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/client-requests/${req.id}`)}
                          className="h-8 w-8 p-0 text-zinc-500 hover:text-sky-400"
                          title="View details"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openSettings(req)}
                          className="h-8 w-8 p-0 text-zinc-500 hover:text-zinc-50"
                          title="Settings"
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteRequest(req.id)}
                          className="h-8 w-8 p-0 text-zinc-500 hover:text-red-400"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Request Settings Dialog */}
      <Dialog open={!!settingsRequest} onOpenChange={() => setSettingsRequest(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Request Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Response Time (hours)</Label>
              <Input
                type="number"
                value={requestSettings.response_time_hours}
                onChange={(e) => setRequestSettings({ ...requestSettings, response_time_hours: parseInt(e.target.value) })}
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Penalty Rate (% per day)</Label>
              <Input
                type="number"
                step="0.1"
                value={requestSettings.penalty_rate_per_day}
                onChange={(e) => setRequestSettings({ ...requestSettings, penalty_rate_per_day: parseFloat(e.target.value) })}
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Grace Period (days)</Label>
              <Input
                type="number"
                value={requestSettings.grace_period_days}
                onChange={(e) => setRequestSettings({ ...requestSettings, grace_period_days: parseInt(e.target.value) })}
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setSettingsRequest(null)} className="border-zinc-700">
                Cancel
              </Button>
              <Button onClick={saveSettings} className="bg-orange-600 hover:bg-orange-700">
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WaitingManagementPage;
